import React from 'react';

/**
 * Radar/Spider chart visualization for Event Genome
 */
export default function GenomeRadar({ genome, size = 300 }) {
  if (!genome) return null;

  const dimensions = [
    { key: 'networking_density', label: 'Networking', color: '#f97316' },
    { key: 'learning_depth', label: 'Learning', color: '#3b82f6' },
    { key: 'deal_making_score', label: 'Deals', color: '#10b981' },
    { key: 'innovation_quotient', label: 'Innovation', color: '#8b5cf6' },
    { key: 'seniority_mix', label: 'Seniority', color: '#ec4899' },
    { key: 'global_reach', label: 'Global', color: '#06b6d4' },
  ];

  const centerX = size / 2;
  const centerY = size / 2;
  const radius = size * 0.35;
  const angleStep = (2 * Math.PI) / dimensions.length;

  // Generate polygon points
  const getPoint = (index, value) => {
    const angle = index * angleStep - Math.PI / 2;
    const r = (value / 100) * radius;
    return {
      x: centerX + r * Math.cos(angle),
      y: centerY + r * Math.sin(angle)
    };
  };

  const points = dimensions.map((dim, i) => getPoint(i, genome[dim.key] || 0));
  const polygonPoints = points.map(p => `${p.x},${p.y}`).join(' ');

  // Grid circles
  const gridLevels = [25, 50, 75, 100];

  return (
    <div className="relative">
      <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`}>
        {/* Grid circles */}
        {gridLevels.map(level => (
          <circle
            key={level}
            cx={centerX}
            cy={centerY}
            r={(level / 100) * radius}
            fill="none"
            stroke="#e5e7eb"
            strokeWidth="1"
          />
        ))}

        {/* Axis lines */}
        {dimensions.map((dim, i) => {
          const endPoint = getPoint(i, 100);
          return (
            <line
              key={dim.key}
              x1={centerX}
              y1={centerY}
              x2={endPoint.x}
              y2={endPoint.y}
              stroke="#e5e7eb"
              strokeWidth="1"
            />
          );
        })}

        {/* Data polygon */}
        <polygon
          points={polygonPoints}
          fill="rgba(249, 115, 22, 0.2)"
          stroke="#f97316"
          strokeWidth="2"
        />

        {/* Data points */}
        {points.map((point, i) => (
          <circle
            key={i}
            cx={point.x}
            cy={point.y}
            r="4"
            fill="#f97316"
          />
        ))}

        {/* Labels */}
        {dimensions.map((dim, i) => {
          const labelPoint = getPoint(i, 120);
          return (
            <text
              key={dim.key}
              x={labelPoint.x}
              y={labelPoint.y}
              textAnchor="middle"
              dominantBaseline="middle"
              className="text-xs font-medium fill-gray-600"
            >
              {dim.label}
            </text>
          );
        })}
      </svg>

      {/* Score legend */}
      <div className="absolute bottom-2 right-2 text-xs text-gray-500">
        Overall: {genome.overall_score}/100
      </div>
    </div>
  );
}

/**
 * Simple bar chart for genome comparison
 */
export function GenomeComparisonBars({ genome1, genome2, name1, name2 }) {
  const dimensions = [
    { key: 'networking_density', label: 'Networking' },
    { key: 'learning_depth', label: 'Learning' },
    { key: 'deal_making_score', label: 'Deal-Making' },
    { key: 'innovation_quotient', label: 'Innovation' },
    { key: 'seniority_mix', label: 'C-Suite Access' },
    { key: 'global_reach', label: 'Global Reach' },
    { key: 'value_for_money', label: 'Value' },
  ];

  return (
    <div className="space-y-4">
      {dimensions.map(dim => {
        const score1 = genome1?.[dim.key] || 0;
        const score2 = genome2?.[dim.key] || 0;
        const winner = score1 > score2 ? 1 : (score2 > score1 ? 2 : 0);

        return (
          <div key={dim.key}>
            <div className="flex justify-between text-sm mb-1">
              <span className="text-gray-700 font-medium">{dim.label}</span>
              <span className="text-gray-500">
                {score1} vs {score2}
              </span>
            </div>
            <div className="flex gap-1 h-4">
              <div className="flex-1 bg-gray-100 rounded-l overflow-hidden">
                <div
                  className={`h-full ${winner === 1 ? 'bg-orange-500' : 'bg-orange-300'} rounded-l transition-all`}
                  style={{ width: `${score1}%` }}
                />
              </div>
              <div className="flex-1 bg-gray-100 rounded-r overflow-hidden flex justify-end">
                <div
                  className={`h-full ${winner === 2 ? 'bg-blue-500' : 'bg-blue-300'} rounded-r transition-all`}
                  style={{ width: `${score2}%` }}
                />
              </div>
            </div>
          </div>
        );
      })}

      {/* Legend */}
      <div className="flex justify-between text-sm mt-4 pt-4 border-t">
        <div className="flex items-center gap-2">
          <div className="w-3 h-3 bg-orange-500 rounded" />
          <span className="text-gray-600">{name1}</span>
        </div>
        <div className="flex items-center gap-2">
          <span className="text-gray-600">{name2}</span>
          <div className="w-3 h-3 bg-blue-500 rounded" />
        </div>
      </div>
    </div>
  );
}